import type { DocumentosModeloItem } from '@/contexts/DocumentosContext';

export type ValorDoQuadro =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'pronto'; valor: number };

export interface LadoDosTotais {
  /** Os mesmos modelos que a aba soma — os visíveis depois do filtro de modelo. */
  modelos: DocumentosModeloItem[];
  carregando: boolean;
  erro: boolean;
}

export interface TotaisDoTopo {
  totalEntradas: ValorDoQuadro;
  totalSaidas: ValorDoQuadro;
  valorEntradas: ValorDoQuadro;
  valorSaidas: ValorDoQuadro;
}

const CARREGANDO: ValorDoQuadro = { estado: 'carregando' };
const ERRO: ValorDoQuadro = { estado: 'erro' };

/** Soma em centavos inteiros: somar reais em ponto flutuante desvia na casa do centavo. */
function somarEmCentavos(valores: number[]): number {
  return valores.reduce((acc, valor) => acc + Math.round(valor * 100), 0) / 100;
}

function totaisDoLado(lado: LadoDosTotais): { quantidade: ValorDoQuadro; valor: ValorDoQuadro } {
  if (lado.carregando) return { quantidade: CARREGANDO, valor: CARREGANDO };
  if (lado.erro) return { quantidade: ERRO, valor: ERRO };

  const quantidade = lado.modelos.reduce((acc, modelo) => acc + modelo.total, 0);

  // Grupo com nota e sem valor somado é uma API que ainda não publica `valorTotal`: zero seria mentira.
  const semValor = lado.modelos.some(
    modelo => modelo.total > 0 && typeof modelo.valorTotal !== 'number'
  );

  return {
    quantidade: { estado: 'pronto', valor: quantidade },
    valor: semValor
      ? ERRO
      : { estado: 'pronto', valor: somarEmCentavos(lado.modelos.map(m => m.valorTotal ?? 0)) },
  };
}

/**
 * Os quatro quadros do topo de Documentos fiscais, calculados das mesmas listas que as abas contam
 * (SPEC-003 da reforma-turbosped). Não recebe a aba aberta: trocar de aba não muda os quadros.
 */
export function calcularTotaisDoTopo(entradas: LadoDosTotais, saidas: LadoDosTotais): TotaisDoTopo {
  const ladoEntradas = totaisDoLado(entradas);
  const ladoSaidas = totaisDoLado(saidas);

  return {
    totalEntradas: ladoEntradas.quantidade,
    totalSaidas: ladoSaidas.quantidade,
    valorEntradas: ladoEntradas.valor,
    valorSaidas: ladoSaidas.valor,
  };
}
